import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { ANALYSIS_SLOTS } from '../../lib/analysis-models';
import { AnalysisModelDraft } from '../../queries/analysisModels';

interface AnalysisModelFormDialogProps {
  draft: AnalysisModelDraft;
  onChange: (draft: AnalysisModelDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

export function AnalysisModelFormDialog({ draft, onChange, onCancel, onSave, saving, error }: AnalysisModelFormDialogProps) {
  const isEdit = !!draft.id;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit model' : 'Add model'}</Dialog.Title>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="col-span-2 text-sm">
              Label
              <input
                className={inputClass}
                value={draft.label ?? ''}
                onChange={(e) => onChange({ ...draft, label: e.target.value })}
                placeholder="Grok 4"
              />
            </label>
            <label className="text-sm">
              AI model
              <input
                className={inputClass}
                value={draft.ai_model ?? ''}
                onChange={(e) => onChange({ ...draft, ai_model: e.target.value })}
                placeholder="Grok"
              />
            </label>
            <label className="text-sm">
              AI version
              <input
                className={inputClass}
                value={draft.ai_version ?? ''}
                onChange={(e) => onChange({ ...draft, ai_version: e.target.value })}
                placeholder="Fast Reasoning"
              />
            </label>
            <label className="col-span-2 text-sm">
              Webhook path
              <input
                className={`${inputClass} font-mono`}
                value={draft.webhook_path ?? ''}
                onChange={(e) => onChange({ ...draft, webhook_path: e.target.value })}
                placeholder="robot-analysis-grok-4"
              />
            </label>
            <label className="text-sm">
              Default slot
              <select
                className={inputClass}
                value={draft.default_slot ?? 1}
                onChange={(e) => onChange({ ...draft, default_slot: parseInt(e.target.value, 10) })}
              >
                {ANALYSIS_SLOTS.map((s) => (
                  <option key={s} value={s}>
                    Slot {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              Sort order
              <input
                type="number"
                className={inputClass}
                value={draft.sort_order ?? 100}
                onChange={(e) => onChange({ ...draft, sort_order: parseInt(e.target.value, 10) })}
              />
            </label>
            <div className="col-span-2 flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!draft.is_preferred}
                  onChange={(e) => onChange({ ...draft, is_preferred: e.target.checked })}
                />
                Preferred
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!draft.no_market}
                  onChange={(e) => onChange({ ...draft, no_market: e.target.checked })}
                />
                No market odds
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.is_active !== false}
                  onChange={(e) => onChange({ ...draft, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
